import { useState, useEffect } from 'react'

import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import { CustomCheckbox, CustomMenuItem } from './style'
import { Icon } from '@iconify/react'

export interface MultipleSelectItemProps {
  id?: number | string
  value?: number | string
}

interface MultipleSelectProps {
  label?: string
  selectedValues: (string | number)[]
  onSelect: (value: (string | number)[]) => void
  menuItems: MultipleSelectItemProps[]
  width?: string
  clearLabel?: string
  maxSelect?: number
}

const MultipleSelect = ({
  selectedValues,
  menuItems,
  onSelect,
  label,
  width,
  clearLabel,
  maxSelect
}: MultipleSelectProps) => {
  const [displayItems, setDisplayItems] = useState<(string | number)[]>([])

  const handleChange = (event: SelectChangeEvent<(string | number)[]>) => {
    const value = event.target.value
    const isReset = value.at(-1) === -1

    if (isReset) {
      onSelect([])
      return
    }

    const selectedArray = value as (string | number)[]

    // Enforce maxSelect if provided
    if (maxSelect && selectedArray.length > maxSelect) {
      return
    }

    onSelect(selectedArray)
  }

  const isMenuItemChecked = (menuItem: MultipleSelectItemProps): boolean =>
    selectedValues.some((selectedValue) => selectedValue === menuItem.id)

  const isMaxReached = !!maxSelect && selectedValues.length >= maxSelect

  useEffect(() => {
    const stringValues = selectedValues.map(
      (selectedValue) => menuItems.find((item) => item.id === selectedValue)?.value ?? ''
    )
    setDisplayItems(stringValues)
  }, [selectedValues, menuItems])

  return (
    <FormControl sx={{ minWidth: width, width }} size="small">
      <InputLabel id="table-checkbox-cell-label">{label}</InputLabel>
      <Select
        labelId="table-checkbox-cell-label"
        id="table-checkbox-cell"
        multiple
        value={selectedValues}
        onChange={handleChange}
        label={label}
        input={<OutlinedInput label={label} />}
        renderValue={() => displayItems.join(', ')}
        disabled={!menuItems.length}
        MenuProps={{
          PaperProps: {
            style: { maxHeight: '220px' },
          },
        }}
      >
        {menuItems.map((menuItem, index) => {
          const isChecked = isMenuItemChecked(menuItem)
          return (
            <CustomMenuItem
              key={index}
              value={menuItem.id}
              sx={{ px: 1.5, width }}
              disabled={isMaxReached && !isChecked}
            >
              <CustomCheckbox checked={isChecked} />
              <Typography variant="body2" noWrap>
                {menuItem.value}
              </Typography>
            </CustomMenuItem>
          )
        })}
        <Button
          value={-1}
          variant="outlined"
          color="inherit"
          size="small"
          sx={{ display: 'flex', m: 'auto', mt: 1.5 }}
        >
          <Icon fontSize={18} icon="ic:round-clear-all" />
          <Typography ml={1}>{clearLabel}</Typography>
        </Button>
      </Select>
    </FormControl>
  )
}

export default MultipleSelect
